import type { ComponentPropsWithRef, ReactNode } from 'react';
import type { BadgeTone } from './components/Badge/Badge';
import { Icon } from './icons/Icon';
import styles from './TagBadge.module.css';

/**
 * How a tag is drawn: `plain` for a tag with no colour, otherwise one of the four §5 tones. Worked out from the
 * stored colour by `tagLook` (`src/lib/tags/tagLook.ts`), never from a style the server sends.
 */
export type TagLook = 'plain' | BadgeTone;

export interface TagBadgeProps {
    readonly look: TagLook;
    readonly children: string;
    /** A retired tag: a dashed edge, no tint, and the word *Retired* inside the tag. */
    readonly retired?: boolean;
    /**
     * `wrap` (records, pickers, Settings): a long name wraps inside the tag, so nothing scrolls sideways at 320px.
     * `cut` (table rows): one line, the name cut at 15 characters with its full text on hover.
     */
    readonly fit?: 'wrap' | 'cut';
    /** The tag's own control, drawn inside its right end — `TagRemoveButton`. */
    readonly action?: ReactNode;
}

/**
 * A tag (the tag design of the UI revamp preview), the one drawing of a tag on every screen — the record tags
 * sections, the leads table, the Settings › Tags table and the tag editor's colour choice — so a tag looks the same
 * wherever it appears. A tag is a 24px rounded rectangle, never a pill: pills are for statuses and the AI's
 * markers. Its colour is a dot and a tint behind dark text, and its remove control sits inside its right end.
 */
export function TagBadge({ look, children, retired = false, fit = 'wrap', action }: TagBadgeProps) {
    const className = [
        styles.tag,
        styles[look],
        retired ? styles.retired : '',
        fit === 'cut' ? styles.cut : '',
    ]
        .filter((name) => name !== '')
        .join(' ');
    return (
        <span
            className={className}
            data-look={look}
            data-slot="tag"
            title={fit === 'cut' ? children : undefined}
        >
            <span className={styles.name}>{children}</span>
            {retired ? <span className={styles.retiredWord}>Retired</span> : null}
            {action}
        </span>
    );
}

export type TagRemoveButtonProps = Omit<ComponentPropsWithRef<'button'>, 'children' | 'type'> & {
    /** The tag's name: the button is *Remove tag <name>*. */
    readonly name: string;
};

/**
 * A tag's remove control, drawn inside the tag by `TagBadge`'s `action`: a 12px ✕ on a 24 × 24 target (WCAG 2.2
 * SC 2.5.8) that tints on hover, never a separate button beside the tag. Takes a ref, so `PermissionGuard` can
 * wrap it.
 */
export function TagRemoveButton({ name, className, ...props }: TagRemoveButtonProps) {
    return (
        <button
            aria-label={`Remove tag ${name}`}
            className={className === undefined ? styles.remove : `${styles.remove} ${className}`}
            type="button"
            {...props}
        >
            <Icon name="close" size="sm" />
        </button>
    );
}
